/**
 * Sparkline — inline area chart. Pure SVG, no deps.
 *
 * Values are oldest→newest; the chart scales to the container and
 * auto-fits the range with a small padding so a flat line still has
 * visible stroke.
 */

import { useId } from 'react';

export function Sparkline({
  values,
  width = 140,
  height = 36,
  stroke = 'rgb(var(--c-spark))',
  fill = 'rgb(var(--c-spark) / 0.12)',
  className,
}: {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  className?: string;
}) {
  const gradId = useId();
  if (values.length === 0) {
    return <svg width={width} height={height} aria-hidden="true" className={className} />;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 3;
  const innerH = height - pad * 2;
  const innerW = width - pad * 2;

  const points = values.map((v, i) => {
    const x = pad + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
    const y = pad + innerH - ((v - min) / range) * innerH;
    return { x, y };
  });

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const area =
    `${line} L${(pad + innerW).toFixed(1)} ${(pad + innerH).toFixed(1)} ` +
    `L${pad.toFixed(1)} ${(pad + innerH).toFixed(1)} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className={className}
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={fill} />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={line} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
